import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import { TreeToolbar } from "slot-definitions"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { useRef, useLayoutEffect, useEffect, useMemo } from "react"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useReferenceState } from "lib/@hooks/use-reference"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { getAllResolutions } from "./controller/get-all-resolutions.controller"
import { ResolutionListRow } from "./resolution-list-row"
import { ResolutionListFilterDropdowns, useResolutionFilters, applyResolutionFilters } from "./resolution-list-filters"
import { SortableTableCell } from "./sortable-table-cell"
import { criticalityRank } from "./criticality-config"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { NoResults } from "lib/@components/no-results/no-results"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"

const SCROLL_KEY = "resolution-list-scroll"

export function ResolutionsListContent() {
    const resolutions = getAllResolutions.useResults(withInitialValue(IS_LOADING))
    const loading = resolutions === IS_LOADING
    const filters = useResolutionFilters()
    const [search] = useSearchParam("search", "")
    const searchLower = search.toLowerCase()
    const [sortField, setSortField] = useReferenceState("resolution-sort-field", "code")
    const [sortDirection, setSortDirection] = useReferenceState("resolution-sort-direction", "asc")
    const filtered = loading ? [] : applySearch(applyResolutionFilters(resolutions ?? [], filters), searchLower)
    const sorted = useMemo(applySorting, [filtered, sortField, sortDirection])
    const scrollRef = useRef()

    useLayoutEffect(restoreScroll, [])
    useEffect(saveScrollOnUnmount, [])

    if (loading) return <LoadingScreen />

    return (
        <PageBreadcrumbContainer title="Legislation Resolutions">
            <TreeToolbar.Plug>
                <ResolutionListFilterDropdowns />
            </TreeToolbar.Plug>
            <SearchBoxUsingSearchParam
                placeholder="Search by code or title…"
                m={0}
                sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}
            />
            <Box ref={scrollRef} flex={1} sx={{ overflowY: "auto" }}>
                <If truthy={sorted.length}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40, pr: 0 }} />
                                <SortableTableCell
                                    field="code"
                                    label="Code"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <SortableTableCell
                                    field="title"
                                    label="Schedule"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Status</TableCell>
                                <TableCell>Types</TableCell>
                                <SortableTableCell
                                    field="criticality"
                                    label="Criticality"
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                />
                                <TableCell>Changes</TableCell>
                                <TableCell>Decisions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat list={sorted} item={<ResolutionListRow />} keyFn={keyById} />
                        </TableBody>
                    </Table>
                    <Else>
                        <NoResults message="No records match the current filters." />
                    </Else>
                </If>
            </Box>
        </PageBreadcrumbContainer>
    )

    function handleSort(field) {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc")
        } else {
            setSortField(field)
            setSortDirection(field === "criticality" ? "desc" : "asc")
        }
    }

    function applySorting() {
        if (!filtered.length) return filtered
        const dir = sortDirection === "asc" ? 1 : -1
        return [...filtered].sort((a, b) => {
            if (sortField === "criticality") {
                return (criticalityRank(a.criticality) - criticalityRank(b.criticality)) * dir
            }
            const av = (a[sortField] ?? "").toLowerCase()
            const bv = (b[sortField] ?? "").toLowerCase()
            return av.localeCompare(bv) * dir
        })
    }

    function restoreScroll() {
        const saved = sessionStorage.getItem(SCROLL_KEY)
        if (saved && scrollRef.current) {
            scrollRef.current.scrollTop = parseInt(saved, 10)
        }
    }

    function saveScrollOnUnmount() {
        const el = scrollRef.current
        return () => {
            if (el) {
                sessionStorage.setItem(SCROLL_KEY, el.scrollTop)
            }
        }
    }
}

function keyById(item) {
    return item._id
}

function applySearch(items, search) {
    if (!search) return items
    return items.filter(
        (item) => item.code?.toLowerCase().startsWith(search) || item.title?.toLowerCase().includes(search)
    )
}
